import LinearProgress from "@mui/material/LinearProgress";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList } from "react-window";
import { EmployeeItem } from "entitites/employees/ui";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import {
  useGetAllEmployeesQuery,
  useLazyGetAllEmployeesBySearchStringQuery,
} from "entitites/employees/api/employeesApi";
import { useState, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Toast } from "shared";

const listItemSize = 52;

export const MainPageList = () => {
  const [cursor, setCursor] = useState("");
  const { search } = useLocation();
  const [params] = useSearchParams();
  const isSearch = Boolean(
    params.get("status") ||
      params.get("text") ||
      params.get("position") ||
      params.get("statusS4S") ||
      params.get("project")
  );
  const {
    data: posts,
    error,
    isLoading,
    isUninitialized,
    isError,
    isFetching,
  } = useGetAllEmployeesQuery(`${cursor}`);
  const [getEmployees, data] = useLazyGetAllEmployeesBySearchStringQuery();
  useEffect(() => {
    isSearch && getEmployees(`${search}`);
  }, [getEmployees, search, isSearch]);

  if (isLoading || isUninitialized || data.isLoading || data.isFetching) {
    return <LinearProgress sx={{ mt: -0.25, ml: -0.25 }} />;
  }

  if (isError) {
    return <Toast error={JSON.stringify(error)} />;
  }

  if (!posts.length) {
    return (
      <Box p={3} py={1}>
        Нет данных
      </Box>
    );
  }

  return (
    <AutoSizer>
      {({ height, width }: { width: number; height: number }) => (
        <>
          {isFetching && (
            <LinearProgress
              sx={{
                width: width,
                mt: -0.25,
                ml: -0.25,
                position: "absolute",
                bottom: 0,
              }}
            />
          )}
          <Box sx={{ width: width }} key="headers">
            <Box
              display="flex"
              alignItems="center"
              p={1}
              px={2}
              pb={0}
              sx={{
                width: width,
                gap: 2,
                height: "100%",
                borderRadius: "4px",
                cursor: "default",
              }}
            >
              <Box
                whiteSpace="nowrap"
                textOverflow="ellipsis"
                overflow="hidden"
                width={width * 0.15}
                maxWidth={width * 0.15}
                fontSize="16px"
                fontWeight={700}
              >
                ФИО
              </Box>
              <Box
                whiteSpace="nowrap"
                textOverflow="ellipsis"
                overflow="hidden"
                width={width * 0.1}
                maxWidth={width * 0.1}
                fontSize="16px"
                fontWeight={700}
              >
                Статус
              </Box>
              <Box
                display="flex"
                alignItems="center"
                flexWrap="nowrap"
                height="45px"
                textOverflow="ellipsis"
                overflow="hidden"
                width={width * 0.25}
                maxWidth={width * 0.25}
                borderRadius="4px"
                fontSize="16px"
                fontWeight={700}
              >
                Позиция
              </Box>
              <Box
                width={width * 0.15}
                maxWidth={width * 0.15}
                borderRadius="4px"
                display="flex"
                alignItems="center"
              >
                <Typography
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  overflow="hidden"
                  fontSize="16px"
                  fontWeight={700}
                >
                  Рабочая почта
                </Typography>
              </Box>
              <Box
                width={width * 0.1}
                maxWidth={width * 0.1}
                borderRadius="4px"
                display="flex"
                alignItems="center"
                gap={1}
              >
                <Typography
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  overflow="hidden"
                  fontSize="16px"
                  fontWeight={700}
                >
                  Телефон
                </Typography>
              </Box>
              <Box
                width={width * 0.1}
                maxWidth={width * 0.1}
                borderRadius="4px"
                display="flex"
                alignItems="center"
                gap={1}
              >
                <Typography
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  overflow="hidden"
                  fontSize="16px"
                  fontWeight={700}
                >
                  Телеграм
                </Typography>
              </Box>
              <Box
                borderRadius="4px"
                display="flex"
                alignItems="center"
                gap={1}
              >
                <Typography
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  overflow="hidden"
                  fontSize="16px"
                  fontWeight={700}
                  ml={1}
                >
                  Действия
                </Typography>
              </Box>
            </Box>
          </Box>
          <Divider
            sx={{ width: width - 4, mx: 0.25, borderColor: "#D9F3BE" }}
          />
          {isSearch ? (
            <FixedSizeList
              itemData={data.data || []}
              height={height - 54}
              width={width}
              itemSize={listItemSize}
              itemCount={data.data?.length || 0}
              overscanCount={20}
            >
              {({ index, style, data }) =>
                data[index] && (
                  <EmployeeItem
                    style={style}
                    data={data[index]}
                    width={width}
                  />
                )
              }
            </FixedSizeList>
          ) : (
            <FixedSizeList
              onScroll={({ scrollOffset, scrollDirection }) => {
                if (scrollDirection === "forward") {
                  const itemCount = posts.length;
                  const endIndex = Math.min(
                    itemCount - 1,
                    Math.floor((scrollOffset + height + 1500) / listItemSize)
                  );
                  if (
                    endIndex === itemCount - 1 &&
                    !isFetching &&
                    cursor !== posts[posts.length - 1].id
                  ) {
                    setCursor(posts[posts.length - 1].id);
                  }
                }
              }}
              itemData={posts}
              height={height - 54}
              width={width}
              itemSize={listItemSize}
              itemCount={posts.length}
              overscanCount={20}
            >
              {({ index, style, data }) =>
                data[index] && (
                  <EmployeeItem
                    style={style}
                    data={data[index]}
                    width={width}
                  />
                )
              }
            </FixedSizeList>
          )}
        </>
      )}
    </AutoSizer>
  );
};
